import { datadogRum } from "@datadog/browser-rum";
import type { NextWebVitalsMetric } from "next/app";

import { clientEnv } from "../../lib/env";
import { sdkLogger } from "../utils/logger";

// Add initialization guard and better error handling
let isInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Maps Vercel environment to clear Datadog environment names
 * - production → production
 * - preview → staging (Vercel's staging/PR deployments)
 * - development → development
 */
function getDatadogEnvironment(): string {
  const vercelEnv = clientEnv.NEXT_PUBLIC_VERCEL_ENV;
  if (vercelEnv === "preview") return "staging";
  return vercelEnv || "development";
}

export function initializeDatadogRum(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (isInitialized) {
    return Promise.resolve();
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = new Promise((resolve, reject) => {
    const applicationId = clientEnv.NEXT_PUBLIC_DATADOG_APP_ID;
    const clientToken = clientEnv.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

    if (!applicationId || !clientToken) {
      sdkLogger.warn("datadog_rum_init_failed", {
        reason: "missing_credentials",
        hasAppId: !!applicationId,
        hasClientToken: !!clientToken,
      });
      resolve();
      return;
    }

    try {
      datadogRum.init({
        applicationId,
        clientToken,
        site: "datadoghq.com",
        service: "wabasignup",
        env: getDatadogEnvironment(), // Maps Vercel "preview" → "staging"
        version: clientEnv.NEXT_PUBLIC_COMMIT_SHA || undefined,
        sessionSampleRate: 100, // Track all sessions (lightweight, needed for performance metrics)
        sessionReplaySampleRate: 50, // Record only 50% of sessions (expensive)
        defaultPrivacyLevel: "mask-user-input",
        allowedTracingUrls: [/https:\/\/(.*\.)?zapier(-staging)?\.com/],
        trackUserInteractions: true,
        trackResources: process.env.NODE_ENV === "production",
        trackLongTasks: process.env.NODE_ENV === "production",
      });

      isInitialized = true;
      sdkLogger.info("datadog_rum_initialized");
      resolve();
    } catch (error) {
      sdkLogger.error("datadog_rum_init_error", {
        error: error instanceof Error ? error.message : String(error),
      });
      reject(error);
    }
  });

  return initializationPromise;
}

// Custom RUM event tracking for WhatsApp signup flow
export const trackWABASignupEvent = (
  eventName: string,
  properties: Record<string, any> = {},
) => {
  if (!isInitialized) return;

  try {
    datadogRum.addAction(eventName, {
      ...properties,
      service: "wabasignup",
      flow: "whatsapp_business_signup",
      timestamp: new Date().toISOString(),
      session_id: datadogRum.getInternalContext()?.session_id,
      view_id: datadogRum.getInternalContext()?.view?.id,
    });
  } catch (error) {
    // Silently fail - Datadog tracking is non-critical
    if (process.env.NODE_ENV === "development") {
      sdkLogger.warn("failed_to_track_waba_signup_event", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
};

// Track user journey through signup flow
export const trackSignupFlowStep = (
  step: string,
  data: Record<string, any> = {},
) => {
  trackWABASignupEvent("waba_signup_step", {
    step,
    ...data,
    flow_stage: step,
  });
};

// Track errors with context
export const trackSignupError = (
  error: string | Error,
  context: Record<string, any> = {},
) => {
  const errorMessage = typeof error === "string" ? error : error.message;
  const errorStack = error instanceof Error ? error.stack : undefined;

  datadogRum.addError(error, {
    ...context,
    flow: "whatsapp_business_signup",
    error_category: "signup_flow",
  });

  trackWABASignupEvent("waba_signup_error", {
    error: errorMessage,
    error_stack: errorStack,
    ...context,
  });
};

// Track performance timing
export const trackSignupTiming = (
  operation: string,
  duration: number,
  metadata: Record<string, any> = {},
) => {
  trackWABASignupEvent("waba_signup_timing", {
    operation,
    duration_ms: duration,
    ...metadata,
  });
};

// Track Facebook callback data
export const trackFacebookCallback = (params: Record<string, string>) => {
  const sanitizedParams = Object.keys(params).reduce(
    (acc, key) => {
      const value = params[key];
      if (key === "code" && value) {
        acc[key] = `****${value.slice(-4)}`;
      } else if (key === "state" && value) {
        acc[key] = `****${value.slice(-4)}`;
      } else if (
        key.includes("token") ||
        key.includes("secret") ||
        key.includes("key")
      ) {
        acc[key] = `****${value.slice(-4)}`;
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  trackWABASignupEvent("facebook_callback_received", {
    has_code: !!params.code,
    has_error: !!params.error,
    has_state: !!params.state,
    param_count: Object.keys(params).length,
    param_keys: Object.keys(params),
    error_type: params.error || null,
    sanitized_params: sanitizedParams,
  });
};

const vitalsMapper: { [key: string]: string } = {
  // Length of time it takes for the page to start and finish hydrating (in ms)
  "Next.js-hydration": "nextJsHydration",
  // Length of time it takes for a page to start rendering after a route change (in ms)
  "Next.js-route-change-to-render": "nextJsRouteChangeToRender",
  // Length of time it takes for a page to finish render after a route change (in ms)
  "Next.js-render": "nextJsRender",

  // Web Vitals - https://web.dev/vitals/
  CLS: "cumulativeLayoutShift",
  FCP: "firstContentfulPaint",
  FID: "firstInputDelay",
  INP: "interactionToNextPaint",
  LCP: "largestContentfulPaint",
  TTFB: "timeToFirstByte",
};

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
export function nextJsReportWebVitals(metric: NextWebVitalsMetric) {
  const mappedName = vitalsMapper[metric.name] || metric.name;

  if (!isInitialized) {
    if (process.env.NODE_ENV === "development") {
      sdkLogger.debug("datadog_rum_web_vital_skipped", {
        metric: mappedName,
        value: metric.value,
        reason: "Datadog RUM not initialized",
      });
    }
    return;
  }

  datadogRum.addAction(`Web Vital: ${mappedName}`, {
    [mappedName]: metric.value,
  });
}

// Helper function to add custom errors to Datadog RUM
export function addCustomError(error: Error, errorData?: Record<string, any>) {
  if (isInitialized) {
    datadogRum.addError(error, errorData);
  }
}
export function trackWebVital(metric: any) {
  if (!isInitialized) return;

  const mappedName = vitalsMapper[metric.name] || metric.name;

  datadogRum.addTiming(mappedName, metric.value);

  // Also track as custom event for better analysis
  trackWABASignupEvent("web_vital", {
    metric_name: mappedName,
    metric_value: metric.value,
    metric_id: metric.id,
    metric_rating: metric.rating,
  });
}
